import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
} from "../../app/itemGridPreferencesSlice";
import { appSettings } from "../../app/appSettingsSlice";
import {
  mergeVisibleColumns,
  needsPreferencesUpdate,
} from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";

const ManageDiscount = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { getCompCurrency, hasInitiallyLoaded } = useProfileAndSettings();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState([]);
  const [originalDiscountCount, setOriginalDiscountCount] = useState(0);
  const compCurrency = getCompCurrency(); // Get currency from the hook
  const pageKey = "discounts";
  const defaultVisibleColumns = {
    discountName: true,
    discountPercentage: true,
    orderAmount: true,
    amountType: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "ID", label: "ID" };
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastSelectedIndex = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();

  

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Currency symbol mapping (removed fetchCompCurrency as we use the hook now)

  const sortByOptions = [
    { value: "ID", label: "ID" },
    { value: "DiscountName", label: "Discount Name" },
    { value: "OrderAmount", label: "Order Amount" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const navigate = useNavigate();

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (
        needsPreferencesUpdate(
          preferences.visibleColumns,
          defaultVisibleColumns
        )
      ) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(
          preferences.visibleColumns,
          defaultVisibleColumns
        );
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const fetchDiscounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DISCOUNTS, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      const discountData = response.data.data || response.data;

      if (Array.isArray(discountData)) {
        setDiscounts(discountData);
        if (!search.trim()) {
          setOriginalDiscountCount(discountData.length);
        }
      } else {
        setDiscounts([]);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to load discounts.");
    }
  }, [search, sortBy, sortOrder, CompID]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDiscounts();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchDiscounts]);

  const handleAddDiscount = () => navigate("/discount/adddiscount");

  const handleDeleteDiscount = async (discountItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete discount ${discountItem.discountName}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!discountItem.id || !CompID) {
        showErrorToast("Invalid discount or company ID.");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_DISCOUNT, {
        params: {
          CompID,
          id: discountItem.id,
        },
      });

      if (response.data.status) {
        setDiscounts((prevDiscounts) =>
          prevDiscounts.filter((item) => item.id !== discountItem.id)
        );
        showSuccessToast("Discount deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong while deleting the discount.");
      setLoading(false);
    }
  };

  const handleSelectItem = (discountId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (discounts[i]?.id) {
            newSelected.add(discounts[i].id);
          }
        }
      } else {
        if (newSelected.has(discountId)) {
          newSelected.delete(discountId);
        } else {
          newSelected.add(discountId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(discounts.map((item) => item.id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No discounts selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected discounts?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((discountId) =>
          axiosInstance.delete(APIs.DELETE_DISCOUNT, {
            params: {
              CompID,
              id: discountId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchDiscounts();
      showSuccessToast(`${selectedItems.size} discounts deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected discounts.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No discounts selected for editing.");
      return;
    }

    const selectedDiscountIds = Array.from(selectedItems);
    navigate(`/discount/bulkEdit`, { state: { selectedDiscountIds } });
  };

  const renderNoDataMessage = () => {
    if (search.trim() && discounts.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No discounts found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Discount</li>
          </ol>
          <h1 className="page-header mb-0">Manage Discount</h1>
        </div>

        <div className="ms-auto">
          {!isChildBranch && (
            <Link to="/discount/adddiscount">
              <button
                className="btn px-4"
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: `0 2px 8px ${themeColor}40`
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }}
              >
                <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
                Add Discount
              </button>
            </Link>
          )}
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search by discount name..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) =>
                                dispatch(setSortBy({ pageKey, sortBy: val }))
                              }
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) =>
                                dispatch(
                                  setSortOrder({ pageKey, sortOrder: val })
                                )
                              }
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(
                                  setVisibleColumns({ pageKey, columns: cols })
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Add Discount Button - Below Filter Bar */}
                {!isChildBranch && !loading && originalDiscountCount === 0 && (
                  <div className="mb-4">
                    <AddButton
                      onClick={handleAddDiscount}
                      title="Add Discount"
                      fontSize="16px"
                    />
                  </div>
                )}

                {/* Selection Info and Actions (Shown only when discounts are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div
                      className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                      style={{ border: "1px solid #dee2e6" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} discount
                          {selectedItems.size > 1 ? "s" : ""} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        {!isChildBranch && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={handleBulkEdit}
                            title="Edit Selected Discounts"
                          >
                            <i className="fa fa-edit me-1"></i>
                            Edit
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Discounts"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {renderNoDataMessage()}

                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    {discounts.length > 0 && (
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                discounts.length > 0 &&
                                selectedItems.size === discounts.length
                              }
                            />
                          </th>
                          {visibleColumns.discountName && (
                            <th className="pt-2 pb-2 text-center">Name</th>
                          )}
                          {visibleColumns.discountPercentage && (
                            <th className="pt-2 pb-2 text-center">Type</th>
                          )}
                          {visibleColumns.orderAmount && (
                            <th className="pt-2 pb-2 text-center">
                              Order Amount
                            </th>
                          )}
                          {visibleColumns.amountType && (
                            <th className="pt-2 pb-2 text-center">Amount Type</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                    )}
                    <tbody>
                      {discounts.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            {search.trim() ? "No discounts found matching your search criteria." : "No discounts available."}
                          </td>
                        </tr>
                      ) : (
                        discounts.map((discount, index) => (
                        <tr key={discount.id}>
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(discount.id)}
                              onChange={(e) =>
                                handleSelectItem(discount.id, index, e)
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          {visibleColumns.discountName && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/discount/updateDiscount/${discount.id}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {discount.discountName}
                            </td>
                          )}
                          {visibleColumns.discountPercentage && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/discount/updateDiscount/${discount.id}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {discount.discountPercentage}%
                            </td>
                          )}
                          {visibleColumns.orderAmount && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/discount/updateDiscount/${discount.id}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {getCurrencySymbol(compCurrency)}{" "}
                              {(discount?.orderAmount ?? 0).toFixed(2)}
                            </td>
                          )}
                          {visibleColumns.amountType && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/discount/updateDiscount/${discount.id}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {discount.amountType}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Edit Icon */}
                              {!isChildBranch && (
                                <Link
                                  to={`/discount/updateDiscount/${discount.id}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Discount"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </Link>
                              )}

                              {/* Delete Icon */}
                              <button
                                onClick={() => handleDeleteDiscount(discount)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete Discount"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc3545';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#dc3545';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ManageDiscount;
